import {
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
  DOCUMENT,
  inject
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { getAccessibleTextColor } from '@bepatient/shared';
import { NumberRangeQuestionV3 } from '../../../classes/question-numberRange';
import { QuestionBaseV3ComponentDirective } from '../question-base.component';

@Component({
  selector: 'bepatient-input-number-range-v3',
  templateUrl: './input-number-range.component.html',
  styleUrls: ['./input-number-range.component.scss'],
  standalone: false
})
export class InputNumberRangeV3Component
  extends QuestionBaseV3ComponentDirective
  implements DoCheck, OnInit
{
  private sanitizer = inject(DomSanitizer);
  document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);
  private renderer = inject(Renderer2);
  private appRef = inject(ApplicationRef);

  @Input() question: NumberRangeQuestionV3;
  @Input() color: string;
  @Input() isLoadingForm: boolean;

  @ViewChild('tooltip', { read: ElementRef, static: true }) tooltip: ElementRef;

  manualRefresh: EventEmitter<void> = new EventEmitter<void>();

  isRtl = false;
  dirty: boolean;
  isBrowser: boolean;
  steps: Array<any>;
  floorReached: boolean = false;
  maxReached: boolean = false;
  resetMode: boolean = false;
  showSliderValue: boolean;
  sliderValue: number;

  @HostBinding('attr.style')
  get valueAsStyle(): any {
    const value = this.formGroup.get(this.question.key).value;
    if (isNaN(parseInt(value, 10))) {
      const dataColor = '#f1f1f1';
      return this.sanitizer.bypassSecurityTrustStyle(
        `--data-color: ${dataColor}; --data-color-contrast: ${getAccessibleTextColor(
          dataColor
        )}`
      );
    } else {
      // Pin label sits on the study color, so derive its text color from the
      // canonical WCAG-AA utility instead of a hard-coded #fff. Recomputed on
      // change detection, so it tracks light/dark/high-contrast toggles.
      const dataColor = this.color || '#f1f1f1';
      return this.sanitizer.bypassSecurityTrustStyle(
        `--data-color: ${
          this.color
        }; --data-color-contrast: ${getAccessibleTextColor(
          dataColor
        )}; --bepatient-slider-reset-btn: #505050`
      );
    }
  }

  get value(): number {
    const value = this.formGroup.get(this.question.key).value;
    if (!isNaN(parseInt(value, 10))) {
      // this.bulletPosition(value);
    }
    return value;
  }

  constructor() {
    super();
    const document = this.document;

    if (document.dir === 'rtl') {
      this.isRtl = true;
    }
  }

  /**
   * Note: Might be useful when custom slider pointer required
   * */
  // bulletPosition(value): void {
  //   const { min, max } = this.question;
  //   const position = Number(((value - min) * 100) / (max - min));
  //   this.tooltip.nativeElement.style.left = `calc(${position}% + (${
  //     8 - position * 0.15
  //   }px))`;
  // }

  resetHandle(event): void {
    if (event) {
      // Resetting input field
      this.sliderValue = void 0;
      this.formGroup.get(this.question.key).reset();
      this.floorReached = false;
      this.maxReached = false;

      // Marking this field as touched
      this.formGroup.get(this.question.key).markAsTouched();

      // Marking this field as dirty
      this.formGroup.get(this.question.key).markAsDirty();
      this.dirty = true;
      this.resetMode = true;
      this.cdr.detectChanges();
    }
  }

  ngOnInit(): void {
    // Assigning show slider value from question
    this.showSliderValue = this.question.showSliderValue;
    // If no value do not display slider value, to prevent displaying NaN as a current value
    if (this.question && (!this.value || this.value == undefined)) {
      this.showSliderValue = false;
    }
    this.sliderValue = this.formGroup.get(this.question.key).value || void 0;
  }

  onFocus(): void {
    // Focus: Setting reset mode to false
    this.resetMode = false;
  }

  onSlideChangeEnd(event: any): void {
    if (!this.resetMode) {
      // Patching value to slider form field
      this.formGroup.get(this.question.key).patchValue(event.detail.value);
      this.sliderValue = event.detail.value;
    }

    // Condition to make min/max range label bold and show/hide slider value (pin)
    if (this.value == this.question.min) {
      this.floorReached = true;
      this.showSliderValue = false;
    } else if (this.value == this.question.max) {
      this.maxReached = true;
      this.showSliderValue = false;
    } else {
      this.floorReached = false;
      this.maxReached = false;
      this.showSliderValue = this.question.showSliderValue;
    }

    // Marking this field as touched
    this.formGroup.get(this.question.key).markAsTouched();

    // Marking this field as dirty
    this.dirty = this.formGroup.get(this.question.key).dirty;

    // Triggering change detection once the value updated
    this.cdr.detectChanges();
  }

  ngDoCheck(): void {
    // Marking this field as dirty, invoked after the default change-detector runs
    this.dirty = this.formGroup.get(this.question.key).dirty;
  }
}
